/**
 * Multipart message part containing headers and payload stream
 */
export interface MultipartMessage {
  headers: Headers;
  payload: ReadableStream<Uint8Array>;
}

/**
 * Error thrown during multipart parsing
 */
export class MultipartParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MultipartParseError";
  }
}

/**
 * Parser configuration options
 */
export interface ParserOptions {
  /** Maximum size for header buffer (default: 64KB) */
  maxHeaderSize?: number;
  /** Maximum size for boundary buffer (default: 8KB) */
  maxBoundaryBuffer?: number;
}

/**
 * Parser states
 */
const enum ParserState {
  Start = 0,
  AfterBoundary = 1,
  Header = 2,
  Body = 3,
  Done = 4,
}

/**
 * Create a search function for finding patterns in Uint8Array
 */
function createSearch(
  pattern: string,
): (haystack: Uint8Array, start?: number) => number {
  const needle = new TextEncoder().encode(pattern);

  return (haystack: Uint8Array, start = 0) =>
    Buffer.prototype.indexOf.call(haystack, needle, start);
}

/**
 * Create search function for partial tail matches (when boundary spans chunks)
 */
function createPartialTailSearch(
  pattern: string,
): (haystack: Uint8Array) => number {
  const needle = new TextEncoder().encode(pattern);
  const byteIndexes: Record<number, number[]> = {};

  for (let i = 0; i < needle.length; ++i) {
    const byte = needle[i];
    if (byteIndexes[byte] === undefined) byteIndexes[byte] = [];
    byteIndexes[byte].push(i);
  }

  return function (haystack: Uint8Array): number {
    const haystackEnd = haystack.length - 1;
    if (haystack[haystackEnd] in byteIndexes) {
      const indexes = byteIndexes[haystack[haystackEnd]];
      for (let i = indexes.length - 1; i >= 0; --i) {
        for (
          let j = indexes[i], k = haystackEnd;
          j >= 0 && haystack[k] === needle[j];
          --j, --k
        ) {
          if (j === 0) return k;
        }
      }
    }
    return -1;
  };
}

/**
 * Parse headers from raw header bytes
 */
function parseHeaders(headerBytes: Uint8Array): Headers {
  // HTTP headers should be ISO-8859-1 compatible, not UTF-8
  const headerText = new TextDecoder("iso-8859-1").decode(headerBytes);

  // Parse header lines into name-value pairs
  const lines = headerText.trim().split(/\r?\n/);
  const headerInit: [string, string][] = [];

  for (const line of lines) {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const name = line.slice(0, colonIndex).trim();
      const value = line.slice(colonIndex + 1).trim();
      headerInit.push([name, value]);
    }
  }

  return new Headers(headerInit);
}

/**
 * Extract boundary from Content-Type header
 */
export function extractBoundary(contentType: string): string {
  const boundaryMatch = contentType.match(/boundary=(?:"([^"]+)"|([^;]+))/i);
  if (!boundaryMatch) {
    throw new MultipartParseError("No boundary found in Content-Type header");
  }
  return boundaryMatch[1] ?? boundaryMatch[2];
}

/**
 * Async message queue for producer-consumer coordination
 * Provides clean separation between chunk reading/parsing and message yielding
 */
class AsyncMessageQueue {
  private queue: MultipartMessage[] = [];
  private waiters: Array<{
    resolve: (value: MultipartMessage | null) => void;
    reject: (error: Error) => void;
  }> = [];
  private finished = false;
  private cancelled = false;
  private error: Error | null = null;

  /**
   * Producer: Enqueue a message for consumption
   */
  enqueue(message: MultipartMessage): void {
    if (this.finished || this.cancelled) return;

    if (this.waiters.length > 0) {
      // Immediate delivery to waiting consumer
      const waiter = this.waiters.shift()!;
      waiter.resolve(message);
    } else {
      // Queue for later consumption
      this.queue.push(message);
    }
  }

  /**
   * Producer: Signal completion (with optional error)
   */
  finish(error?: Error): void {
    if (this.finished) return;

    this.finished = true;
    this.error = error || null;

    // Notify all waiting consumers
    while (this.waiters.length > 0) {
      const waiter = this.waiters.shift()!;
      if (error) {
        waiter.reject(error);
      } else {
        waiter.resolve(null);
      }
    }
  }

  /**
   * Consumer: Cancel the queue (stops accepting new messages and notifies waiters)
   */
  cancel(): void {
    if (this.cancelled || this.finished) return;

    this.cancelled = true;

    // Notify all waiting consumers of cancellation
    while (this.waiters.length > 0) {
      const waiter = this.waiters.shift()!;
      waiter.resolve(null);
    }
  }

  /**
   * Consumer: Dequeue next message (or null if finished/cancelled)
   */
  async dequeue(): Promise<MultipartMessage | null> {
    // Return queued message if available
    if (this.queue.length > 0) {
      return this.queue.shift()!;
    }

    // Return immediately if finished or cancelled
    if (this.finished || this.cancelled) {
      if (this.error) throw this.error;
      return null;
    }

    // Wait for next message
    return new Promise((resolve, reject) => {
      this.waiters.push({ resolve, reject });
    });
  }

  /**
   * Check if the queue is in a terminal state
   */
  get isTerminal(): boolean {
    return this.finished || this.cancelled;
  }
}

/**
 * Parse a multipart/mixed response stream.
 * Yields messages as they arrive without buffering entire payloads in memory.
 */
export async function* parseMultipartStream(
  response: Response,
  options?: ParserOptions,
): AsyncGenerator<MultipartMessage, void, unknown> {
  if (!response.body) {
    throw new MultipartParseError("Response body is null");
  }

  const contentType = response.headers.get("content-type");
  if (!contentType) {
    throw new MultipartParseError("Missing Content-Type header");
  }

  const boundary = extractBoundary(contentType);
  const parser = new StreamingMultipartParser(boundary, options);

  // Create async generator from the parser
  yield* parser.parseStream(response.body);
}

/**
 * Streaming multipart parser.
 * Yields messages immediately as headers are parsed, with streaming payloads.
 */
class StreamingMultipartParser {
  private readonly boundary: string;
  private readonly findOpeningBoundary: (
    haystack: Uint8Array,
    start?: number,
  ) => number;
  private readonly openingBoundaryLength: number;
  private readonly findBoundary: (
    haystack: Uint8Array,
    start?: number,
  ) => number;
  private readonly findPartialTailBoundary: (haystack: Uint8Array) => number;
  private readonly boundaryLength: number;
  private readonly findDoubleNewline: (
    haystack: Uint8Array,
    start?: number,
  ) => number;

  // Safety limits
  private readonly maxHeaderSize: number;
  private readonly maxBoundaryBuffer: number;

  private state = ParserState.Start;
  private buffer: Uint8Array | null = null;
  private currentHeaders: Headers = new Headers();
  private currentPayloadController: ReadableStreamDefaultController<Uint8Array> | null =
    null;

  constructor(boundary: string, options: ParserOptions = {}) {
    this.boundary = boundary;
    this.findOpeningBoundary = createSearch(`--${boundary}`);
    this.openingBoundaryLength = 2 + boundary.length;
    this.findBoundary = createSearch(`\r\n--${boundary}`);
    this.findPartialTailBoundary = createPartialTailSearch(`\r\n--${boundary}`);
    this.boundaryLength = 4 + boundary.length;
    this.findDoubleNewline = createSearch("\r\n\r\n");

    // Set safety limits
    this.maxHeaderSize = options.maxHeaderSize ?? 65536; // 64KB
    this.maxBoundaryBuffer = options.maxBoundaryBuffer ?? 8192; // 8KB
  }

  async *parseStream(
    stream: ReadableStream<Uint8Array>,
  ): AsyncGenerator<MultipartMessage, void, unknown> {
    const reader = stream.getReader();
    const messageQueue = new AsyncMessageQueue();

    /**
     * Producer: Read chunks and parse messages
     *
     * Runs independently from consumer to prevent deadlocks when message payloads
     * span multiple network chunks. This ensures chunks continue to be read even
     * while consumers are processing messages.
     */
    const producer = this.startProducer(reader, messageQueue);

    try {
      // Consumer: Yield messages from the queue
      yield* this.consumeMessages(messageQueue);
    } finally {
      // Cleanup: Cancel queue and stream, close any open payload controllers
      messageQueue.cancel();
      this.closeCurrentPayload();

      try {
        await reader.cancel();
      } catch (error) {
        // Ignore cancellation errors - stream might already be closed
      }

      // Wait for producer to finish cleanup
      await producer;
    }
  }

  /**
   * Producer: Continuously read chunks and parse messages
   */
  private async startProducer(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    messageQueue: AsyncMessageQueue,
  ): Promise<void> {
    try {
      while (!messageQueue.isTerminal) {
        let result;
        try {
          result = await reader.read();
        } catch (readError) {
          // Handle network interruptions, aborted streams, timeouts, etc.
          if (
            readError instanceof Error &&
            (readError.name === "AbortError" ||
              readError.constructor.name === "AbortError" ||
              readError.name === "TimeoutError" ||
              readError.constructor.name === "TimeoutError")
          ) {
            // Stream was cancelled or timed out, exit cleanly
            break;
          }
          throw readError;
        }

        const { done, value } = result;

        if (done) {
          // Process any remaining buffered content
          if (this.buffer !== null && this.buffer.length > 0) {
            const messages = this.write(new Uint8Array(0));
            for (const message of messages) {
              if (messageQueue.isTerminal) break;
              messageQueue.enqueue(message);
            }
          }

          if (this.state !== ParserState.Done) {
            if (this.state === ParserState.Start) {
              throw new MultipartParseError(
                "Invalid multipart stream: missing initial boundary",
              );
            }
            throw new MultipartParseError("Unexpected end of stream");
          }
          break;
        }

        // Validate chunk data
        if (!(value instanceof Uint8Array)) {
          throw new MultipartParseError(
            `Invalid chunk type: expected Uint8Array, got ${typeof value}`,
          );
        }

        // Process chunk and enqueue any messages that become ready
        const messages = this.write(value);
        for (const message of messages) {
          if (messageQueue.isTerminal) break;
          messageQueue.enqueue(message);
        }
      }

      // Signal successful completion (unless already cancelled)
      if (!messageQueue.isTerminal) {
        messageQueue.finish();
      }
    } catch (error) {
      // Ensure any open payload controller receives the error
      this.closeCurrentPayload(error as Error);

      // Signal error to consumer (unless already cancelled)
      if (!messageQueue.isTerminal) {
        messageQueue.finish(error as Error);
      }
    } finally {
      try {
        reader.releaseLock();
      } catch (error) {
        // Ignore errors during cleanup
      }
    }
  }

  /**
   * Consumer: Yield messages from the queue
   */
  private async *consumeMessages(
    messageQueue: AsyncMessageQueue,
  ): AsyncGenerator<MultipartMessage, void, unknown> {
    while (true) {
      const message = await messageQueue.dequeue();
      if (message === null) {
        // Producer finished
        break;
      }
      yield message;
    }
  }

  /**
   * Process a chunk of data through the state machine and return any complete messages.
   *
   * Returns an array because a single chunk can contain multiple complete messages
   * when small messages with headers + body + boundary all fit in one network chunk.
   * All messages must be captured and queued to maintain proper message ordering.
   */
  private write(chunk: Uint8Array): MultipartMessage[] {
    const newMessages: MultipartMessage[] = [];
    if (this.state === ParserState.Done) {
      throw new MultipartParseError("Unexpected data after end of stream");
    }

    let index = 0;
    let chunkLength = chunk.length;

    // Merge with existing buffer if present, with safety checks
    if (this.buffer !== null) {
      const bufferLength = this.buffer.length;
      const newSize = bufferLength + chunkLength;

      if (this.state === ParserState.Header) {
        if (newSize > this.maxHeaderSize) {
          throw new MultipartParseError(
            `Buffer size limit exceeded: ${newSize} bytes > ${this.maxHeaderSize} bytes. ` +
              `This may indicate malformed multipart data with oversized headers.`,
          );
        }
      } else if (bufferLength > this.maxBoundaryBuffer) {
        throw new MultipartParseError(
          `Boundary buffer limit exceeded: ${bufferLength} bytes > ${this.maxBoundaryBuffer} bytes. ` +
            `This may indicate malformed multipart data with invalid boundaries.`,
        );
      }

      const newChunk = new Uint8Array(newSize);
      newChunk.set(this.buffer, 0);
      newChunk.set(chunk, bufferLength);
      chunk = newChunk;
      chunkLength = chunk.length;
      this.buffer = null;
    }

    // Handle special case: empty chunk at end of stream with buffered content
    if (chunkLength === 0 && this.state === ParserState.Start) {
      throw new MultipartParseError(
        "Invalid multipart stream: missing initial boundary",
      );
    }

    while (true) {
      if (this.state === ParserState.Body) {
        if (chunkLength - index < this.boundaryLength) {
          const remainingData = chunk.subarray(index);
          if (remainingData.length > this.maxBoundaryBuffer) {
            throw new MultipartParseError(
              `Boundary buffer limit exceeded: ${remainingData.length} > ${this.maxBoundaryBuffer}`,
            );
          }
          this.buffer = remainingData;
          break;
        }

        const boundaryIndex = this.findBoundary(chunk, index);
        if (boundaryIndex === -1) {
          const partialTailIndex = this.findPartialTailBoundary(chunk);
          if (partialTailIndex === -1) {
            this.writeBody(index === 0 ? chunk : chunk.subarray(index));
          } else {
            this.writeBody(chunk.subarray(index, partialTailIndex));
            const partialBoundary = chunk.subarray(partialTailIndex);
            if (partialBoundary.length > this.maxBoundaryBuffer) {
              throw new MultipartParseError(
                `Partial boundary too large: ${partialBoundary.length} > ${this.maxBoundaryBuffer}`,
              );
            }
            this.buffer = partialBoundary;
          }
          break;
        }

        this.writeBody(chunk.subarray(index, boundaryIndex));
        this.finishMessage();
        index = boundaryIndex + this.boundaryLength;
        this.state = ParserState.AfterBoundary;
      }

      if (this.state === ParserState.AfterBoundary) {
        if (chunkLength - index < 2) {
          const remainingData = chunk.subarray(index);
          if (remainingData.length > this.maxBoundaryBuffer) {
            throw new MultipartParseError(
              `After-boundary buffer limit exceeded: ${remainingData.length} > ${this.maxBoundaryBuffer}`,
            );
          }
          this.buffer = remainingData;
          break;
        }

        // Check for final boundary
        if (chunk[index] === 45 && chunk[index + 1] === 45) {
          // '--'
          this.state = ParserState.Done;
          break;
        }

        // Skip line ending after boundary - handle both CRLF and LF
        if (chunk[index] === 13 && chunk[index + 1] === 10) {
          // CRLF
          index += 2;
        } else if (chunk[index] === 10) {
          // LF only
          index += 1;
        } else {
          throw new MultipartParseError(
            `Invalid character after boundary: expected CRLF or LF, got 0x${chunk[index].toString(16)}`,
          );
        }
        this.state = ParserState.Header;
      }

      if (this.state === ParserState.Header) {
        if (chunkLength - index < 4) {
          const remainingData = chunk.subarray(index);
          if (remainingData.length > this.maxHeaderSize) {
            throw new MultipartParseError(
              `Header buffer limit exceeded: ${remainingData.length} > ${this.maxHeaderSize}`,
            );
          }
          this.buffer = remainingData;
          break;
        }

        // Try to find double newline with both CRLF and LF patterns
        let headerEndIndex = this.findDoubleNewline(chunk, index);
        let headerEndOffset = 4; // for \r\n\r\n

        if (headerEndIndex === -1) {
          // Try LF-only pattern
          const lfDoubleNewline = createSearch("\n\n");
          headerEndIndex = lfDoubleNewline(chunk, index);
          headerEndOffset = 2; // for \n\n
        }

        if (headerEndIndex === -1) {
          const headerData = chunk.subarray(index);
          if (headerData.length > this.maxHeaderSize) {
            throw new MultipartParseError(
              `Headers too large: ${headerData.length} > ${this.maxHeaderSize} bytes`,
            );
          }
          this.buffer = headerData;
          break;
        }

        const headerBytes = chunk.subarray(index, headerEndIndex);
        this.currentHeaders = parseHeaders(headerBytes);

        // Create a message with streaming payload
        const message = this.createStreamingMessage();
        newMessages.push(message);

        index = headerEndIndex + headerEndOffset;
        this.state = ParserState.Body;
        continue;
      }

      if (this.state === ParserState.Start) {
        if (chunkLength < this.openingBoundaryLength) {
          if (chunk.length > this.maxBoundaryBuffer) {
            throw new MultipartParseError(
              `Initial chunk too large for boundary detection: ${chunk.length} > ${this.maxBoundaryBuffer}`,
            );
          }
          this.buffer = chunk;
          break;
        }

        const boundaryIndex = this.findOpeningBoundary(chunk);

        if (boundaryIndex !== 0) {
          throw new MultipartParseError(
            "Invalid multipart stream: missing initial boundary",
          );
        }

        index = this.openingBoundaryLength;
        this.state = ParserState.AfterBoundary;
      }
    }

    return newMessages;
  }

  private createStreamingMessage(): MultipartMessage {
    const headers = new Headers(this.currentHeaders);

    const payload = new ReadableStream<Uint8Array>({
      start: (controller) => {
        this.currentPayloadController = controller;
      },
    });

    // Reset headers for next message
    this.currentHeaders = new Headers();

    return {
      headers,
      payload,
    };
  }

  private writeBody(chunk: Uint8Array): void {
    if (this.currentPayloadController) {
      this.currentPayloadController.enqueue(chunk);
    }
  }

  private finishMessage(): void {
    if (this.currentPayloadController) {
      this.currentPayloadController.close();
      this.currentPayloadController = null;
    }
  }

  /**
   * Close current payload controller if open (used during cleanup)
   * If an error is provided, forwards it to the payload consumer
   */
  private closeCurrentPayload(error?: Error): void {
    if (this.currentPayloadController) {
      try {
        if (error) {
          this.currentPayloadController.error(error);
        } else {
          this.currentPayloadController.close();
        }
      } catch (controllerError) {
        // Ignore errors if controller is already closed/errored
      }
      this.currentPayloadController = null;
    }
  }
}
